import { BookUser, Plus } from "lucide-react";
import { useEffect, useMemo, useState, type FC } from "react";

import {
  CheckboxField,
  CountryField,
  DateField,
  FormRow,
  FormSheet,
  TextareaField,
  TextField
} from "@/components/form";

import { ForeignNationalsAPI } from "../../../../api/foreignNationals";
import type {
  ForeignNationalPassport,
  PassportPayload
} from "../../../../api/types/foreignNationalRecords";
import { SourceCell } from "../../components/SourceTag";
import {
  ClientTable,
  type ClientTableColumn
} from "../../components/table/ClientTable";
import { ClientTableRow } from "../../components/table/ClientTableRow";
import { ClientTableToolbar } from "../../components/table/ClientTableToolbar";
import {
  BadgeCell,
  DateCell,
  MutedCell,
  SkeletonRow,
  TitleCell,
  type BadgeVariant
} from "../../components/table/cells";
import { useClientProfile } from "../../context/ClientProfileContext";
import { useRecordCrud } from "../../hooks/useRecordCrud";
import { NAV_ITEMS } from "../../navConfig";
import {
  emptyPassportDraft,
  passportDraftSchema,
  passportDraftToPayload,
  passportFocusDomId,
  passportState,
  passportToDraft,
  sortPassports,
  type PassportDraft,
  type PassportState
} from "./passportsModel";

const getId = (p: ForeignNationalPassport) => p.passport_id;

const COLUMNS: ClientTableColumn[] = [
  { id: "number", header: "Passport" },
  { id: "country", header: "Issuing country" },
  { id: "issued", header: "Issued" },
  { id: "expires", header: "Expires" },
  { id: "state", header: "State", className: "w-30" },
  { id: "source", header: "Source", className: "w-30" }
];

const STATE_BADGE: Record<
  PassportState,
  { label: string; variant: BadgeVariant }
> = {
  current: { label: "Current", variant: "success" },
  expiring: { label: "Expiring", variant: "warning" },
  expired: { label: "Expired", variant: "error" },
  previous: { label: "Previous", variant: "neutral" }
};

/** Every passport the client has held; the current one feeds forms and reminders. */
export const PassportsTab: FC = () => {
  const { clientId, reloadClient } = useClientProfile();
  const [search, setSearch] = useState("");

  useEffect(() => setSearch(""), [clientId]);

  const crud = useRecordCrud<
    ForeignNationalPassport,
    PassportDraft,
    PassportPayload
  >({
    clientId,
    noun: "Passport",
    list: ForeignNationalsAPI.listPassports,
    create: ForeignNationalsAPI.createPassport,
    update: ForeignNationalsAPI.updatePassport,
    remove: ForeignNationalsAPI.deletePassport,
    getId,
    toDraft: passportToDraft,
    emptyDraft: emptyPassportDraft,
    toPayload: passportDraftToPayload,
    schema: passportDraftSchema,
    sort: sortPassports,
    afterMutate: () => reloadClient()
  });

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return crud.records
      .map((p) => ({
        entry: p,
        id: getId(p),
        state: passportState(p),
        haystack: `${p.passport_number} ${p.issuing_country ?? ""} ${
          p.notes ?? ""
        }`.toLowerCase()
      }))
      .filter((r) => !q || r.haystack.includes(q));
  }, [crud.records, search]);

  const isEditing = crud.editor.mode === "edit";

  return (
    <div className="flex h-full min-h-0 w-full flex-col">
      <ClientTableToolbar
        title={NAV_ITEMS.passports.label}
        description="The current passport fills forms and drives the 270 and 180 day renewal reminders."
        search={{
          value: search,
          onChange: setSearch,
          placeholder: "Search passports...",
          ariaLabel: "Search passports"
        }}
        onRefresh={() => crud.refresh().catch(() => undefined)}
        isRefreshing={crud.isLoading}
        primaryAction={{
          label: "Add Passport",
          onClick: () =>
            crud.openCreate({
              isCurrent: crud.records.length === 0 || undefined
            })
        }}
      />
      <ClientTable
        ariaLabel="Passports"
        columns={COLUMNS}
        isLoading={crud.isLoading}
        isEmpty={rows.length === 0}
        error={crud.error}
        onRetry={() => crud.refresh().catch(() => undefined)}
        renderSkeletonRow={(i) => (
          <SkeletonRow
            key={i}
            widths={["w-32", "w-28", "w-24", "w-24", "w-16", "w-16"]}
          />
        )}
        empty={
          crud.records.length === 0
            ? {
                title: "No Passports Yet",
                icon: BookUser,
                message:
                  "Add the client's passport so forms, travel checks and renewal reminders have it.",
                cta: "Add Passport",
                ctaIcon: Plus,
                onCta: () => crud.openCreate({ isCurrent: true })
              }
            : {
                title: "No Matching Passports",
                icon: BookUser,
                message: "Try a different search."
              }
        }
      >
        {rows.map((row) => (
          <ClientTableRow
            key={row.id}
            domId={passportFocusDomId(row.id)}
            onActivate={() => crud.openEdit(row.entry)}
          >
            <TitleCell
              icon={BookUser}
              iconClassName={
                row.entry.is_current ? "bg-brand-50 text-brand-600" : undefined
              }
              title={row.entry.passport_number || "Number not recorded"}
            />
            <MutedCell>{row.entry.issuing_country || "—"}</MutedCell>
            <DateCell value={row.entry.issue_date} />
            <DateCell value={row.entry.expiration_date} />
            <BadgeCell
              label={STATE_BADGE[row.state].label}
              variant={STATE_BADGE[row.state].variant}
            />
            <SourceCell source={row.entry.source} />
          </ClientTableRow>
        ))}
      </ClientTable>

      <FormSheet
        form={crud.form}
        open={crud.editor.mode !== "closed"}
        onOpenChange={(open) => {
          if (!open) crud.close();
        }}
        title={isEditing ? "Edit passport" : "Add passport"}
        description="Exactly as printed on the passport's data page."
        onSubmit={crud.submit}
        isEditing={isEditing}
        isSubmitting={crud.isSaving}
        isDeleting={crud.isDeleting}
        onDelete={() => {
          crud.deleteCurrent().catch(() => undefined);
        }}
        deleteTitle="Delete this passport?"
      >
        <FormRow>
          <TextField<PassportDraft>
            name="passportNumber"
            label="Passport Number"
            placeholder="e.g. A12345678"
            autoComplete="off"
          />
          <CountryField<PassportDraft>
            name="issuingCountry"
            label="Issuing Country"
          />
        </FormRow>
        <FormRow>
          <DateField<PassportDraft> name="issueDate" label="Date of Issue" />
          <DateField<PassportDraft>
            name="expirationDate"
            label="Date of Expiration"
          />
        </FormRow>
        <CheckboxField<PassportDraft>
          name="isCurrent"
          label="This is the passport the client travels on now"
        />
        <TextareaField<PassportDraft>
          name="notes"
          label="Notes"
          placeholder="Lost, renewed, held by the consulate..."
        />
      </FormSheet>
    </div>
  );
};
